import React, { useContext, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import {
  ACCOUNT_DELETION_REASON_LABELS,
  AccountDeletionReason,
  ACCOUNT_DELETION_REASON_LIST,
  DeleteAccountPayload,
  emailsMatch,
} from '@ecountabl/lib'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Ionicons } from '@expo/vector-icons'
import { ShadowView, textStyles } from '../style/style'
import { ThemeContext } from '../utility/context/ThemeContext'
import { useAuthContext } from '../utility/context'
import { useAppSelector } from '../store'
import KeyboardAvoidingScrollView from '../utility/keyboard-avoiding-scrollview'
import { MAX_CONTENT_WIDTH } from '../config'
import { ThemedTextInput } from '../utility/input/textinputs/themed-text-input'
import { ThemedCheckBox } from '../utility/input/misc/themed-checkbox'
import { ThemedButton } from '../utility/input/buttons'
import ThemedDialog from '../style/dialog'
import Analytics from '../utility/analytics'
import { log } from '../utility'
import { goBack } from '../root-navigation'

type DeleteAccountReasonRowProps = {
  reason: AccountDeletionReason
  checked: boolean
  disabled: boolean
  onToggle: (reason: AccountDeletionReason) => void
}

const DeleteAccountReasonRow = ({
  reason,
  checked,
  disabled,
  onToggle,
}: DeleteAccountReasonRowProps): React.ReactElement => (
  <ThemedCheckBox
    label={ACCOUNT_DELETION_REASON_LABELS[reason]}
    checked={checked}
    disabled={disabled}
    onChange={() => onToggle(reason)}
  />
)

const DeleteAccount = (): React.ReactElement => {
  const { theme } = useContext(ThemeContext)
  const { textMain, bgMain, bgSecond, borderColor, danger, textSecond } = theme.colors
  const { bottom } = useSafeAreaInsets()
  const { deleteAccount } = useAuthContext()
  const { user } = useAppSelector((state) => state.auth)

  const accountEmail = user?.email ?? ''

  const [selectedReasons, setSelectedReasons] = useState<AccountDeletionReason[]>([])
  const [otherReason, setOtherReason] = useState('')
  const [details, setDetails] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<Error | null>(null)

  const includesOther = selectedReasons.includes(AccountDeletionReason.Other)
  const emailMatches = accountEmail.length > 0 && emailsMatch(confirmEmail, accountEmail)

  const canSubmit = useMemo(() => {
    if (!accountEmail || submitting) {
      return false
    }
    if (selectedReasons.length === 0) {
      return false
    }
    if (!details.trim()) {
      return false
    }
    if (includesOther && !otherReason.trim()) {
      return false
    }
    if (!emailMatches) {
      return false
    }
    return true
  }, [
    accountEmail,
    submitting,
    selectedReasons.length,
    details,
    includesOther,
    otherReason,
    emailMatches,
  ])

  const handleCancel = (): void => {
    goBack()
  }

  const toggleReason = (reason: AccountDeletionReason): void => {
    setSubmitError(null)
    setSelectedReasons((current) => {
      if (current.includes(reason)) {
        return current.filter((value) => value !== reason)
      }
      return [...current, reason]
    })
  }

  const buildPayload = (): DeleteAccountPayload => ({
    email: confirmEmail.trim(),
    reasons: selectedReasons,
    details: details.trim(),
    otherReason: includesOther ? otherReason.trim() : undefined,
  })

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return
    }

    const payload = buildPayload()
    const analyticsPayload = {
      reasons: payload.reasons,
      reasonCount: payload.reasons.length,
      hasOtherReason: includesOther,
      hasDetails: details.trim().length > 0
    }

    setSubmitting(true)
    setSubmitError(null)

    Analytics.track('delete_account_submitted', analyticsPayload)

    try {
      await deleteAccount(payload)
      Analytics.track('delete_account_completed', analyticsPayload)
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String((error as { message: unknown }).message)
            : 'Unable to delete your account. Please try again.'

      log.error(error instanceof Error ? error : new Error(message), {
        tags: { message: 'error submitting account deletion' },
      })
      setSubmitError(message)
      setDialogError(error instanceof Error ? error : new Error(message))
    } finally {
      setSubmitting(false)
    }
  }

  if (!accountEmail) {
    return (
      <View style={{ flex: 1, backgroundColor: bgMain }}>
        <KeyboardAvoidingScrollView
          contentContainerStyle={{
            flexGrow: 1,
            padding: 16,
            paddingBottom: bottom + 24,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <View style={{ width: '100%', maxWidth: MAX_CONTENT_WIDTH, gap: 16 }}>
            <Text style={[textStyles.body, { color: textMain, textAlign: 'center' }]}>
              {'We could not load your account email. Return to Settings and try again, or contact support if this continues.'}
            </Text>
            <ThemedButton text="Back to Settings" onPress={handleCancel} variant="primary" />
          </View>
        </KeyboardAvoidingScrollView>
      </View>
    )
  }

  return (
    <View style={{ flex: 1, backgroundColor: bgMain }}>
      <KeyboardAvoidingScrollView
        extraScrollHeight={bottom * -2}
        contentContainerStyle={{
          flexGrow: 1,
          padding: 16,
          paddingBottom: bottom + 24,
          alignItems: 'center',
        }}
      >
        <View style={{ width: '100%', maxWidth: MAX_CONTENT_WIDTH, gap: 20 }}>
          <View style={{ gap: 8 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Close your account</Text>
            <Text style={[textStyles.body, { color: textSecond }]}>
              {'This permanently deletes your Impact Banking account, linked financial accounts, and associated transaction history. This cannot be undone.'}
            </Text>
          </View>

          <ShadowView
            style={{
              width: '100%',
              padding: 16,
              gap: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderColor,
              backgroundColor: bgSecond,
            }}
          >
            <Text style={[textStyles.h3, { color: textMain }]}>Why are you leaving?</Text>
            <Text style={[textStyles.body, { color: textSecond }]}>
              Select all that apply. Your feedback helps us improve.
            </Text>
            <View style={{ gap: 10 }}>
              {ACCOUNT_DELETION_REASON_LIST.map((reason) => (
                <DeleteAccountReasonRow
                  key={reason}
                  reason={reason}
                  checked={selectedReasons.includes(reason)}
                  disabled={submitting}
                  onToggle={toggleReason}
                />
              ))}
            </View>
            {includesOther && (
              <ThemedTextInput
                label="Tell us more about your other reason"
                value={otherReason}
                onChange={setOtherReason}
                placeholder="Describe your reason"
                required
                disabled={submitting}
                textInputProps={{
                  multiline: false,
                  maxLength: 250
                }}
              />
            )}
          </ShadowView>

          <ShadowView
            style={{
              width: '100%',
              padding: 16,
              gap: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderColor,
              backgroundColor: bgSecond,
            }}
          >
            <ThemedTextInput
              label="Additional details"
              value={details}
              onChange={setDetails}
              placeholder="Share anything else that would help us understand your decision"
              required
              disabled={submitting}
              textInputProps={{
                multiline: true,
                maxLength: 2000,
                numberOfLines: 5,
                style: { height: 120, textAlignVertical: 'top' },
              }}
            />
          </ShadowView>

          <ShadowView
            style={{
              width: '100%',
              padding: 16,
              gap: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderColor,
              backgroundColor: bgSecond,
            }}
          >
            <Text style={[textStyles.h3, { color: textMain }]}>Confirm your email</Text>
            <Text style={[textStyles.body, { color: textSecond }]}>
              Type <Text style={{ fontWeight: '600', color: textMain }}>{accountEmail}</Text> to confirm you want to close this account.
            </Text>
            <ThemedTextInput
              label="Email address"
              value={confirmEmail}
              onChange={setConfirmEmail}
              placeholder={accountEmail}
              required
              disabled={submitting}
              textInputProps={{
                autoComplete: 'email',
                keyboardType: 'email-address',
                autoCapitalize: 'none',
              }}
              errorText={
                confirmEmail.length > 0 && !emailMatches
                  ? 'Email must match your account email exactly'
                  : undefined
              }
            />
          </ShadowView>

          {submitError && (
            <Text style={[textStyles.subscript, { color: danger }]}>{submitError}</Text>
          )}

          <View style={{ gap: 10, paddingTop: 8 }}>
            {!canSubmit && !submitting && (
              <Text style={[textStyles.subscript, { color: textSecond, textAlign: 'center' }]}>
                Complete all required fields and confirm your email to continue.
              </Text>
            )}
            <ThemedButton
              text="Permanently delete my account"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
              variant="primary"
              style={canSubmit ? { backgroundColor: danger, borderWidth: 0 } : undefined}
            />
            <ThemedButton
              text="Cancel"
              onPress={handleCancel}
              variant="cancel"
              disabled={submitting}
            />
          </View>
        </View>
      </KeyboardAvoidingScrollView>

      <ThemedDialog
        visible={!!dialogError}
        errorAnimation={!!dialogError}
        title="Unable to delete account"
        subMessage={dialogError?.message || 'An unexpected error occurred.'}
        options={[
          {
            title: 'Okay',
            onPress: () => setDialogError(null),
            role: 'destructive',
            icon: <Ionicons name="checkmark" size={22} />,
          },
        ]}
      />
    </View>
  )
}

export default DeleteAccount
